import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { communicationIntegrationsService } from "@/services/communication-integrations-service";
import {
  CommunicationIntegration,
  CommunicationIntegrationKind,
  IntegrationHealthProbeResult,
  UpsertEmailIntegrationRequest,
  UpsertMetaWhatsappIntegrationRequest,
  UpsertTwilioIntegrationRequest,
} from "@/types/communication-integration";
import { messagingChannelConfigKeys } from "@/components/pages/communication-channels/hooks/messaging-channel-configs.hooks";
import { whatsappKeys } from "@/hooks/whatsapp.hooks";
import { whatsappProvisioningKeys } from "./whatsapp-provisioning.hooks";

export const communicationIntegrationKeys = {
  all: ["communicationIntegrations"] as const,
  list: (hotelId: string) =>
    [...communicationIntegrationKeys.all, "list", hotelId] as const,
  detail: (hotelId: string, kind: CommunicationIntegrationKind) =>
    [...communicationIntegrationKeys.all, "detail", hotelId, kind] as const,
};

export const useCommunicationIntegrations = (hotelId: string | null) =>
  useQuery<CommunicationIntegration[]>({
    queryKey: communicationIntegrationKeys.list(hotelId ?? ""),
    queryFn: () => communicationIntegrationsService.list(hotelId as string),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });

const invalidateIntegrationsAndChannels = (
  qc: ReturnType<typeof useQueryClient>,
  hotelId: string,
) => {
  qc.invalidateQueries({
    queryKey: communicationIntegrationKeys.list(hotelId),
  });
  qc.invalidateQueries({
    queryKey: messagingChannelConfigKeys.list(hotelId),
  });
};

const invalidateWhatsAppOperations = (
  qc: ReturnType<typeof useQueryClient>,
  hotelId: string,
) => {
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.flowStatus(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.botReadiness(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.templates(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.templateBindings(hotelId),
  });
  qc.invalidateQueries({
    queryKey: whatsappProvisioningKeys.templateBlueprints(hotelId, "en_US"),
  });
  qc.invalidateQueries({
    queryKey: whatsappKeys.templates(hotelId),
  });
};

export const useUpsertEmailIntegration = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    CommunicationIntegration,
    unknown,
    UpsertEmailIntegrationRequest
  >({
    mutationFn: (payload) =>
      communicationIntegrationsService.upsertEmail(hotelId, payload),
    onSuccess: () => invalidateIntegrationsAndChannels(qc, hotelId),
  });
};

export const useUpsertTwilioIntegration = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    CommunicationIntegration,
    unknown,
    UpsertTwilioIntegrationRequest
  >({
    mutationFn: (payload) =>
      communicationIntegrationsService.upsertTwilio(hotelId, payload),
    onSuccess: () => invalidateIntegrationsAndChannels(qc, hotelId),
  });
};

export const useUpsertWhatsappIntegration = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    CommunicationIntegration,
    unknown,
    UpsertMetaWhatsappIntegrationRequest
  >({
    mutationFn: (payload) =>
      communicationIntegrationsService.upsertWhatsapp(hotelId, payload),
    onSuccess: () => {
      invalidateIntegrationsAndChannels(qc, hotelId);
      invalidateWhatsAppOperations(qc, hotelId);
    },
  });
};

interface SetEnabledArgs {
  kind: CommunicationIntegrationKind;
  enabled: boolean;
}

export const useSetIntegrationEnabled = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<CommunicationIntegration, unknown, SetEnabledArgs>({
    mutationFn: ({ kind, enabled }) =>
      communicationIntegrationsService.setEnabled(hotelId, kind, enabled),
    onSuccess: (_result, variables) => {
      invalidateIntegrationsAndChannels(qc, hotelId);
      if (variables.kind === "META_WHATSAPP") {
        invalidateWhatsAppOperations(qc, hotelId);
      }
    },
  });
};

export const useDeleteIntegration = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<void, unknown, CommunicationIntegrationKind>({
    mutationFn: (kind) =>
      communicationIntegrationsService.remove(hotelId, kind),
    onSuccess: (_result, kind) => {
      invalidateIntegrationsAndChannels(qc, hotelId);
      if (kind === "META_WHATSAPP") {
        invalidateWhatsAppOperations(qc, hotelId);
      }
    },
  });
};

export const useTestIntegration = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<
    IntegrationHealthProbeResult,
    unknown,
    CommunicationIntegrationKind
  >({
    mutationFn: (kind) => communicationIntegrationsService.test(hotelId, kind),
    onSuccess: (_result, kind) => {
      invalidateIntegrationsAndChannels(qc, hotelId);
      if (kind === "META_WHATSAPP") {
        invalidateWhatsAppOperations(qc, hotelId);
      }
    },
  });
};
